import { z } from "zod";

import {
  createMutationRequestDefinition,
  createQueryRequestDefinition,
} from "~/lib/network/definitions/create";

import { subscribablePlansEnumSchema } from "../checkout/network/schema";
import { roleSchema } from "../teams/network/schema";

import { loginCredentialsSchema } from "./credentials";

export const exchangeCodeForSessionMutation = createMutationRequestDefinition({
  host: "cap-srv",
  method: "GET",
  path: "/auth/workos/callback",
  paramsSchema: z.object({
    code: z.string(),
    state: z.string().optional(),
  }),
  responseSchema: loginCredentialsSchema,
  assumeUnAuthenticated: true,
});

export const getAuthRedirectUrlMutation = createMutationRequestDefinition({
  host: "cap-srv",
  method: "GET",
  path: "/auth/workos/authorize",
  paramsSchema: z.object({ callbackBaseUrl: z.string() }),
  responseSchema: z.object({
    url: z.string(),
  }),
  assumeUnAuthenticated: true,
});

export const logoutUserMutation = createMutationRequestDefinition({
  host: "cap-srv",
  method: "POST",
  path: "/auth/workos/logout",
  paramsSchema: z.object({}),
  responseSchema: z.object({}),
});

export const workosAuthenticationResponseSchema = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),

  userId: z.string(),
});

export const refreshAccessTokenMutation = createMutationRequestDefinition({
  host: "cap-srv",
  method: "POST",
  path: "/auth/workos/refresh",
  // NOTE: requires refresh token in authorization header
  paramsSchema: z.object({
    oldAccessToken: z.string(),
  }),
  responseSchema: workosAuthenticationResponseSchema,
  assumeUnAuthenticated: true,
});

export const GetUserProfileRequestSchema = z.object({});

export const orgSubscriptionDetailsSchema = z.object({
  plan: subscribablePlansEnumSchema,
  frequency: z.enum(["monthly", "yearly"]),
  includedSeats: z.number(),
  purchasedSeats: z.number(),
  cancelAtPeriodEnd: z.boolean(),
  // we might know this for enterprise
  currentPeriodEnd: z.string().nullable(),
});
export type OrgSubscriptionDetailsType = z.infer<typeof orgSubscriptionDetailsSchema>;

const userProfileOrgSchema = z.object({
  id: z.string(),
  name: z.string(),
  thumbnailUrl: z.string(),
  subscription: orgSubscriptionDetailsSchema.nullable(),
});
export type UserProfileOrgType = z.infer<typeof userProfileOrgSchema>;

const userProfileSchema = z.object({
  userId: z.string(),
  name: z.string(),
  email: z.string(),
  permissions: z.array(z.string()),
  metadata: z.record(z.string(), z.any()),
  role: roleSchema,
});
export type UserProfileType = z.infer<typeof userProfileSchema>;

export const getUserProfileResponseSchema = z.object({
  orgProfile: userProfileOrgSchema,
  userProfile: userProfileSchema,
});
export type GetUserProfileResponseType = z.infer<typeof getUserProfileResponseSchema>;

export const getUserQuery = createQueryRequestDefinition({
  queryKey: ["user", "profile"],
  host: "cap-srv",
  method: "GET",
  path: "/mirage-studio/user-profile",
  responseSchema: getUserProfileResponseSchema,
});

export const acceptTermsOfServiceMutation = createMutationRequestDefinition({
  host: "cap-srv",
  method: "POST",
  path: "/mirage-studio/user/accept-terms-of-service",
  paramsSchema: z.object({ version: z.enum(["v1"]) }),
  responseSchema: z.object({}),
});

export const getBillingPortalUrlMutation = createMutationRequestDefinition({
  host: "cap-srv",
  method: "GET",
  path: "/mirage-studio/org/billing-portal-url",
  paramsSchema: z.object({
    returnUrl: z.string(),
  }),
  responseSchema: z.object({
    url: z.string().nullable(),
  }),
});

export const switchOrganizationMutation = createMutationRequestDefinition({
  host: "cap-srv",
  method: "POST",
  path: "/auth/workos/switch-organization",
  paramsSchema: z.object({
    orgId: z.string(),
    refreshToken: z.string(),
  }),
  responseSchema: workosAuthenticationResponseSchema,
});
